import { Component, computed, input, output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup } from '@angular/forms';
import { FieldsetModule } from 'primeng/fieldset';
import { DatePickerModule } from 'primeng/datepicker';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TooltipModule } from 'primeng/tooltip';
import { ButtonModule } from 'primeng/button';

import { ContactoAdicional } from 'src/app/domain/model/cumplimiento/cumplimiento';
import { esContactoObligatorio } from 'src/app/common/contacto-obligatorio.utils';
import {
  LOCALE_NUMERICO,
  MAX_CORREO_CONTACTO,
  MAX_DECIMALES_CANTIDAD,
  MAX_NOMBRE_CONTACTO
} from 'src/app/common/constants-ejb';
import { DecimalComaDirective } from 'src/app/common/directives/decimal-coma.directive';
import { DecimalHintComponent } from 'src/app/presentation/shared/decimal-hint/decimal-hint.component';
import {
  emailKeypress,
  sanitizeEmailContacto,
  sanitizeNombreContacto
} from 'src/app/common/input-utils';

/**
 * DUMB COMPONENT: DatosCumplimiento
 *
 * Panel con los campos editables del cumplimiento para tipo CAMPO.
 *
 * Legacy: CreateOrUpdateCumplimientoViewImpl.ui.xml líneas 176-265
 * Orden de campos:
 *   1. Fecha Cumplimiento | Hora de inicio
 *   2. Cantidad Ejecutada | Duración
 *   3. Nombre Contacto | Correo Contacto | Adicionar contactos
 *   4. Atención Virtual (S/N)
 *   5. Número de Asistentes
 */
@Component({
  selector: 'app-datos-cumplimiento',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FieldsetModule,
    DatePickerModule,
    InputNumberModule,
    InputTextModule,
    RadioButtonModule,
    TooltipModule,
    ButtonModule,
    DecimalComaDirective,
    DecimalHintComponent
  ],
  template: `
    <p-fieldset
      legend="Datos Cumplimiento"
      [toggleable]="true"
      styleClass="mb-4"
    >
      <div [formGroup]="form()">
        <!-- Fila 1: Cantidad Ejecutada | Nombre Contacto | Correo Contacto | Adicionar contactos -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-1">
              Cantidad Ejecutada <span class="text-red-500">*</span>
            </label>
            <p-inputNumber
              appDecimalComa
              formControlName="cantidadEjecutada"
              pTooltip="Ingrese la cantidad de horas ejecutadas"
              [locale]="localeNumerico"
              [useGrouping]="false"
              [minFractionDigits]="0"
              [maxFractionDigits]="maxDecimalesCantidad"
              [min]="0"
              ariaDescribedBy="ayuda-cantidad-ejecutada"
              inputStyleClass="w-full"
            >
            </p-inputNumber>
            @if (form().get('cantidadEjecutada')?.hasError('max')) {
              <small class="text-red-500">Máximo: {{ saldoActual() }}</small>
            } @else {
              <app-decimal-hint id="ayuda-cantidad-ejecutada" ejemplo="1,5" />
            }
          </div>

          @if (esTipoCampo()) {
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Nombre Contacto
                @if (contactoObligatorio()) {
                  <span class="text-red-500">*</span>
                }
              </label>
              <input
                pInputText
                formControlName="nombreContacto"
                (change)="onNombreContactoInput()"
                [attr.maxlength]="maxNombreContacto"
                class="w-full"
                pTooltip="Ingrese el nombre del Contacto"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Correo Contacto
                @if (contactoObligatorio()) {
                  <span class="text-red-500">*</span>
                }
              </label>
              <input
                pInputText
                formControlName="correoContacto"
                type="email"
                (keypress)="emailKeypress($event)"
                (change)="onCorreoContactoInput()"
                [attr.maxlength]="maxCorreoContacto"
                class="w-full"
                pTooltip="Ingrese el correo del contacto"
              />
            </div>
            <div class="flex items-end gap-2">
              <a
                class="text-blue-600 hover:text-blue-800 cursor-pointer text-sm underline"
                (click)="abrirContactosDialog.emit()"
              >
                Adicionar contactos
              </a>
              @if (mostrarLupaContacto() && !isCumpComplementarios()) {
                <button
                  pButton
                  type="button"
                  icon="pi pi-search"
                  class="p-button-outlined p-button-sm min-w-[44px] min-h-[44px]"
                  (click)="abrirBuscadorContactos.emit()"
                  pTooltip="Buscar contacto"
                ></button>
              }
            </div>
          }
        </div>

        <!-- Fila 2 (solo tipo CAMPO): Número de Asistentes | Atención Virtual -->
        @if (esTipoCampo()) {
          <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
            @if (mostrarNumeroAsistentes()) {
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">
                  Número de Asistentes
                  @if (asistentesRequeridos()) {
                    <span class="text-red-500">*</span>
                  }
                </label>
                <p-inputNumber
                  formControlName="numeroAsistentes"
                  [pTooltip]="
                    readonlyNumeroAsistentes()
                      ? 'Calculado automáticamente a partir de la lista de asistentes'
                      : 'Ingrese el número de asistentes'
                  "
                  [useGrouping]="false"
                  [minFractionDigits]="0"
                  [maxFractionDigits]="0"
                  [min]="1"
                  [readonly]="readonlyNumeroAsistentes()"
                  inputStyleClass="w-full"
                >
                </p-inputNumber>
                @if (readonlyNumeroAsistentes()) {
                  <small class="text-gray-500 block mt-1"
                    >Se calcula desde la lista de asistentes del acta.</small
                  >
                }
              </div>
            }
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Atención Virtual <span class="text-red-500">*</span>
              </label>
              <div class="flex items-center gap-6">
                <div class="flex items-center gap-2">
                  <p-radioButton
                    formControlName="snatencionVirtual"
                    value="S"
                    inputId="virtualSi"
                    (onClick)="atencionVirtualChange.emit('S')"
                  >
                  </p-radioButton>
                  <label for="virtualSi" class="cursor-pointer">Si</label>
                </div>
                <div class="flex items-center gap-2">
                  <p-radioButton
                    formControlName="snatencionVirtual"
                    value="N"
                    inputId="virtualNo"
                    (onClick)="atencionVirtualChange.emit('N')"
                  >
                  </p-radioButton>
                  <label for="virtualNo" class="cursor-pointer">No</label>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </p-fieldset>
  `
})
export class DatosCumplimientoComponent {
  readonly localeNumerico = LOCALE_NUMERICO;
  readonly maxDecimalesCantidad = MAX_DECIMALES_CANTIDAD;

  // === Inputs ===
  form = input.required<FormGroup>();
  saldoActual = input<number>(0);
  esTipoCampo = input<boolean>(false);
  esTipoInforme = input<boolean>(false);
  isCumpComplementarios = input<boolean>(false);
  readonly maxNombreContacto = MAX_NOMBRE_CONTACTO;
  readonly maxCorreoContacto = MAX_CORREO_CONTACTO;
  /** Asterisco de Nombre/Correo: misma regla que validarFormulario. */
  readonly contactoObligatorio = computed(() =>
    esContactoObligatorio({ esTipoCampo: this.esTipoCampo() })
  );
  /** Legacy (CreateOrUpdateCumplimientoViewImpl:744): la lupa de contactos solo se muestra sin programación. */
  mostrarLupaContacto = input<boolean>(true);
  mostrarNumeroAsistentes = input<boolean>(true);
  asistentesRequeridos = input<boolean>(false);
  /**
   * Cuando true, el campo "Número de Asistentes" se muestra read-only.
   * Legacy: CreateOrUpdateCumplimientoViewImpl.configurarCamposActaServicios()
   *   tbNumeroAsistentes.setReadOnly(true) — para Acta Asesoría, se autocalcula
   *   desde la lista de asistentes (bloqueAsistentes).
   */
  readonlyNumeroAsistentes = input<boolean>(false);

  readonly emailKeypress = emailKeypress;

  /**
   * Legacy (getTextBoxChangeHandlerPreserveCase + REGEX_NAMES): solo letras y espacios, sin tildes ni ñ.
   * La barrera real es @SafeText(PERSON_NAME) en backend. Se conservan los espacios para nombres compuestos.
   */
  onNombreContactoInput(): void {
    this.sanitizarControl('nombreContacto', sanitizeNombreContacto);
  }

  /** Legacy: el correo del contacto conserva mayúsculas/minúsculas (ver `sanitizeEmailContacto`). */
  onCorreoContactoInput(): void {
    this.sanitizarControl('correoContacto', sanitizeEmailContacto);
  }

  private sanitizarControl(nombre: string, sanitizar: (valor: string) => string): void {
    const control = this.form().get(nombre);
    const valor = control?.value;
    if (typeof valor !== 'string') {
      return;
    }
    const limpio = sanitizar(valor);
    if (limpio !== valor) {
      control?.setValue(limpio);
    }
  }

  // === Outputs ===
  abrirContactosDialog = output<void>();
  abrirBuscadorContactos = output<void>();
  atencionVirtualChange = output<string>();
}
